import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown,
  Hand,
  ImageIcon,
  Moon,
  SquareMousePointer,
  Sun,
  TypeIcon,
} from "lucide-react";
import Mousetrap from "mousetrap";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useLayoutPresets from "@/components/AppHeader/hooks/useLayoutPresets";
import useToggleDriveMode from "@/components/AppHeader/hooks/useToggleDriveMode";
import Icon, { ICONS } from "@/components/Core/Icons";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useKeyboardShortcuts } from "@/hooks/keyboard-shortcuts";
import { cn } from "@/lib/utils";
import {
  CursorType,
  ModalType,
  revertCursorType,
  setCursorType,
  setModal,
} from "@/store/globalSettingsSlice";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";
import useLayoutStore, { LayoutMode } from "@/store/zustand/layoutStore";
import { isTargetTextInput } from "@/utils/clipboardUtils";
import { useCanvasId } from "@/utils/utils";
import GenerateImageInput from "../AI/GenerateImageInput";
import GenerateInput from "../AI/GenerateInput";
import { useDesignTokensStore } from "../DesignTokens/store/designTokensStore";
import FrameOptions from "./components/FrameOptions";
import { InteractDesignToggle } from "./components/InteractDesignToggle";
import TypographyOptions from "./components/TypographyOptions";
import {
  DESIGN_TOOLBAR_SHORTCUTS,
  getTooltipContent,
} from "./constants/designToolbarShortcuts";
import { useDesignToolbarShortcuts } from "./hooks/useDesignToolbarShortcuts";

function DesignToolsPicker() {
  const { options: layoutOptions } = useLayoutPresets();
  const dispatch = useDispatch();
  const showModal = useSelector((state: any) => state.globalSettings.showModal);

  // Hover indicator state
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [hoverState, setHoverState] = useState<{
    x: number;
    width: number;
    height: number;
  } | null>(null);
  const [isHovering, setIsHovering] = useState(false);

  const handleButtonHover = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const button = e.currentTarget;
      const toolbar = toolbarRef.current;
      if (!toolbar) return;

      const toolbarRect = toolbar.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();

      setHoverState({
        x: buttonRect.left - toolbarRect.left,
        width: buttonRect.width,
        height: buttonRect.height,
      });
      setIsHovering(true);
    },
    [],
  );

  // Hide hover shape instantly on toggle click
  const handleToggleClick = useCallback(() => {
    setIsHovering(false);
    setHoverState(null);
  }, []);

  const handleToolbarMouseLeave = useCallback(() => {
    setIsHovering(false);
  }, []);

  const layoutMode = useLayoutStore((state) => state.layoutMode);

  const selectedLayoutOption = useSelector(
    (state: any) => state.globalSettings.selectedLayoutOption,
  );

  const selectedLayoutOptionToUse = useMemo(() => {
    return selectedLayoutOption || layoutOptions[0];
  }, [selectedLayoutOption, layoutOptions]);

  const isSpacePressed = useSelector(
    (state: any) => state.globalSettings.isSpacePressed,
  );

  const cursorType = useSelector(
    (state: any) => state.globalSettings.cursorType,
  );

  // Clear hover state immediately when entering interact mode
  useEffect(() => {
    if (cursorType === CursorType.Drive) {
      setIsHovering(false);
      setHoverState(null);
    }
  }, [cursorType]);

  const modalActive = showModal !== ModalType.None;

  const cursorTypeToUse = isSpacePressed ? CursorType.Pan : cursorType;

  const addContextMode = useLayoutStore((state) => state.addContextMode);

  // Get hooks for dark mode functionality
  const canvasId = useCanvasId();
  const firstLoadDone = useDesignTokensStore((state) => state.firstLoadDone);
  // Get current canvas dark mode state
  const canvasDarkMode = useDarkModeStore(
    (state) => state.canvasDarkModeStates[canvasId] || false,
  );

  const { handleShortcut } = useDesignToolbarShortcuts();
  const { toggleCursor } = useToggleDriveMode({ fromKey: true });

  const shortcuts = useMemo(
    () =>
      Object.entries(DESIGN_TOOLBAR_SHORTCUTS)
        .filter(([key]) => key !== "DESIGN_INTERACT_MODE")
        .map(([key, shortcut]) => ({
          key: shortcut.key,
          handler: (e: KeyboardEvent) => handleShortcut(key, e),
          preventDefault: true,
          description: shortcut.description,
        })),
    [handleShortcut],
  );

  useKeyboardShortcuts({
    shortcuts,
  });

  useEffect(() => {
    const checkTextInput = (e: KeyboardEvent) =>
      isTargetTextInput(e.target as HTMLElement);

    Mousetrap.bind("d", (e) => {
      if (checkTextInput(e) || e.repeat) return;
      toggleCursor();
    });

    Mousetrap.bind(
      "d",
      (e) => {
        if (checkTextInput(e)) return;
        if (cursorType === CursorType.Drive) dispatch(revertCursorType());
      },
      "keyup",
    );

    return () => {
      Mousetrap.unbind("d");
      Mousetrap.unbind("d", "keyup");
    };
  }, [cursorType, toggleCursor, dispatch]);

  const LayoutIcon = useMemo(() => {
    return selectedLayoutOptionToUse.icon;
  }, [selectedLayoutOptionToUse]);

  // Canvas dark mode toggle handler
  const handleCanvasDarkModeToggle = () => {
    const newDarkMode = !canvasDarkMode;

    // Update canvas dark mode state
    useDarkModeStore.getState().setCanvasDarkMode(canvasId, newDarkMode);
  };

  return (
    <nav className="flex flex-col gap-1 min-w-[308px]">
      <AnimatePresence>
        {showModal === ModalType.AI && (
          <motion.div
            key="generate-input"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <GenerateInput
              addContextMode={layoutMode === LayoutMode.PRD && addContextMode}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <div className="flex justify-center">
        {/** biome-ignore lint/a11y/noStaticElementInteractions: presentation only (hover effect) */}
        <div
          ref={toolbarRef}
          onMouseLeave={handleToolbarMouseLeave}
          className={cn(
            "min-h-10 w-fit flex items-center px-2 py-2 rounded-lg gap-1 pointer-events-auto bg-secondary-0 shadow-[0_8px_12px_-4px_rgba(0,0,0,0.5),inset_0_1px_2px_rgba(255,255,255,0.04)] border-[0.5px] border-white/8 relative",
            isSpacePressed && "pointer-events-none cursor-grab",
            showModal === ModalType.Image && "bg-transparent",
          )}
        >
          {/* Smooth hover indicator - hidden immediately when entering/exiting interact mode */}
          <AnimatePresence>
            {cursorType !== CursorType.Drive && (
              <motion.div
                className="absolute rounded-md bg-white/8 pointer-events-none top-2 z-0"
                initial={{ opacity: 0 }}
                animate={{
                  left: hoverState?.x ?? 0,
                  width: hoverState?.width ?? 0,
                  height: hoverState?.height ?? 0,
                  opacity: isHovering && hoverState ? 1 : 0,
                }}
                exit={{ opacity: 0, transition: { duration: 0 } }}
                transition={{
                  left: { type: "spring", stiffness: 400, damping: 30 },
                  width: { type: "spring", stiffness: 400, damping: 30 },
                  height: { type: "spring", stiffness: 400, damping: 30 },
                  opacity: { duration: 0 },
                }}
              />
            )}
          </AnimatePresence>
          <AnimatePresence mode="wait" initial={false}>
            {showModal === ModalType.Image ? (
              <motion.div
                key="image-input"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              >
                <GenerateImageInput />
              </motion.div>
            ) : (
              <motion.div
                key="toolbar-buttons"
                initial="hidden"
                animate="visible"
                exit="hidden"
                className="flex items-center"
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Button
                    size="icon"
                    variant={showModal === ModalType.AI ? "primary" : "ghost"}
                    data-tooltip-id="tooltip"
                    data-tooltip-html={getTooltipContent(
                      "AI",
                      DESIGN_TOOLBAR_SHORTCUTS.AI.key,
                    )}
                    data-tooltip-hidden={
                      showModal === ModalType.AI ||
                      layoutMode === LayoutMode.Code
                    }
                    data-mouse-down-id="design-tool-button"
                    className="group px-1.5 w-auto h-7 rounded-md relative z-10 hover:bg-transparent"
                    onMouseEnter={handleButtonHover}
                    onClick={() => {
                      if (showModal === ModalType.AI) {
                        dispatch(setModal(ModalType.None));
                      } else {
                        dispatch(setModal(ModalType.AI));
                        dispatch(
                          setCursorType({
                            cursorType: CursorType.Pointer,
                          }),
                        );
                      }
                    }}
                  >
                    <Icon
                      icon={ICONS.AI_LOGO}
                      className={cn(
                        "transition-colors opacity-70 group-hover:opacity-100 w-4 h-4",
                        {
                          "opacity-100": showModal === ModalType.AI,
                        },
                      )}
                    />
                  </Button>
                </motion.div>
                {/* First divider - always visible, hides first on contract, shows last on expand */}
                <AnimatePresence mode="sync">
                  <motion.div
                    key="divider-1"
                    initial={{ opacity: 0, scaleY: 0 }}
                    animate={{
                      opacity: 1,
                      scaleY: 1,
                      transition: {
                        delay: cursorType === CursorType.Drive ? 0.3 : 0,
                        duration: 0.25,
                      },
                    }}
                    exit={{
                      opacity: 0,
                      scaleY: 0,
                      transition: { duration: 0.08 },
                    }}
                    className="w-px self-stretch -my-2 mx-1 bg-white/5"
                  />
                </AnimatePresence>
                {/* Middle container with smooth width animation */}
                <motion.div
                  className="relative flex items-center justify-center px-1 overflow-hidden w-[248px] pl-[8px] pr-[8px]"
                  initial={false}
                  animate={{
                    width: cursorType === CursorType.Drive ? 72 : 248,
                  }}
                  transition={{
                    width: {
                      duration: 0.4,
                      ease: [0.25, 0.1, 0.25, 1],
                    },
                  }}
                >
                  <AnimatePresence mode="wait">
                    {cursorType === CursorType.Drive ? (
                      <motion.div
                        key="drive-mode-container"
                        className="flex items-center justify-center gap-2"
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 12 }}
                        transition={{
                          duration: 0.3,
                          ease: [0.4, 0, 0.2, 1],
                          opacity: { duration: 0.2 },
                        }}
                      >
                        <Button
                          variant={
                            !modalActive && cursorTypeToUse === CursorType.Pan
                              ? "primary"
                              : "ghost"
                          }
                          size="icon"
                          data-tooltip-id="tooltip"
                          data-tooltip-html={getTooltipContent(
                            "Hand tool",
                            DESIGN_TOOLBAR_SHORTCUTS.HAND.key,
                          )}
                          data-mouse-down-id="design-tool-button"
                          className={cn(
                            "px-1.5 w-auto h-7 text-zinc-400 rounded-md relative z-10 hover:bg-transparent",
                            {
                              "text-foreground":
                                !modalActive &&
                                cursorTypeToUse === CursorType.Pan,
                            },
                          )}
                          onMouseEnter={handleButtonHover}
                          onClick={() => {
                            dispatch(setModal(ModalType.None));
                            dispatch(
                              setCursorType({
                                cursorType: CursorType.Pan,
                              }),
                            );
                          }}
                        >
                          <Hand className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-tooltip-id="tooltip"
                          data-tooltip-html={
                            !firstLoadDone
                              ? "Loading design tokens..."
                              : getTooltipContent(
                                  `Switch to ${canvasDarkMode ? "light" : "dark"} mode`,
                                  DESIGN_TOOLBAR_SHORTCUTS
                                    .TOGGLE_DARK_LIGHT_MODE.key,
                                )
                          }
                          data-mouse-down-id="design-tool-button"
                          className={cn(
                            "group px-1.5 w-auto h-7 rounded-md relative z-10 hover:bg-transparent",
                            !firstLoadDone && " cursor-not-allowed",
                          )}
                          onMouseEnter={handleButtonHover}
                          onClick={
                            !firstLoadDone
                              ? undefined
                              : handleCanvasDarkModeToggle
                          }
                          aria-label={`Switch to ${
                            canvasDarkMode ? "light" : "dark"
                          } mode`}
                        >
                          {canvasDarkMode ? (
                            <Sun className="w-4 h-4 text-zinc-400 group-hover:text-foreground transition-colors" />
                          ) : (
                            <Moon className="w-4 h-4 text-zinc-400 group-hover:text-foreground transition-colors" />
                          )}
                        </Button>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="expanded-icons-container"
                        className="flex items-center gap-2"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{
                          duration: 0.3,
                          ease: [0.4, 0, 0.2, 1],
                          opacity: { duration: 0.2 },
                        }}
                      >
                        <Button
                          size="icon"
                          variant={
                            !modalActive &&
                            cursorTypeToUse === CursorType.Pointer
                              ? "primary"
                              : "ghost"
                          }
                          data-tooltip-id="tooltip"
                          data-tooltip-html={getTooltipContent(
                            "Select",
                            DESIGN_TOOLBAR_SHORTCUTS.SELECT.key,
                          )}
                          data-mouse-down-id="design-tool-button"
                          className={cn(
                            "px-1.5 w-auto h-7 text-zinc-400 rounded-md relative z-10 hover:bg-transparent",
                            {
                              "text-foreground":
                                !modalActive &&
                                cursorTypeToUse === CursorType.Pointer,
                            },
                          )}
                          onMouseEnter={handleButtonHover}
                          onClick={() => {
                            dispatch(setModal(ModalType.None));
                            dispatch(
                              setCursorType({
                                cursorType: CursorType.Pointer,
                              }),
                            );
                          }}
                        >
                          <SquareMousePointer className="h-4 w-4" />
                        </Button>
                        <div className="flex items-center">
                          <Button
                            size="icon"
                            variant={
                              (!modalActive &&
                                cursorTypeToUse === CursorType.Draw) ||
                              showModal === ModalType.Frame
                                ? "primary"
                                : "ghost"
                            }
                            data-testid="design-tools-draw-button"
                            data-tooltip-id="tooltip"
                            data-tooltip-html={getTooltipContent(
                              "Draw",
                              DESIGN_TOOLBAR_SHORTCUTS.DRAW.key,
                            )}
                            data-mouse-down-id="design-tool-button"
                            className={cn(
                              "px-1.5 w-8 h-7 text-zinc-400 rounded-md relative z-10 hover:bg-transparent",
                              {
                                "text-foreground":
                                  (!modalActive &&
                                    cursorTypeToUse === CursorType.Draw) ||
                                  showModal === ModalType.Frame,
                              },
                            )}
                            onMouseEnter={handleButtonHover}
                            onClick={() => {
                              dispatch(setModal(ModalType.None));
                              dispatch(
                                setCursorType({
                                  cursorType: CursorType.Draw,
                                }),
                              );
                            }}
                          >
                            <LayoutIcon className="w-4 h-4" strokeWidth={2.5} />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              onKeyDown={(e) => e.preventDefault()}
                              asChild
                            >
                              <Button
                                size="icon"
                                variant="ghost"
                                data-testid="design-tools-frame-options-dropdown"
                                data-tooltip-id="tooltip"
                                data-tooltip-content="Drawing options"
                                className="px-0 w-auto h-7 text-zinc-400 rounded-md relative z-10 hover:bg-transparent"
                                onMouseEnter={handleButtonHover}
                              >
                                <ChevronDown className="w-3 h-3 transition-all duration-300 ease-in-out" />
                              </Button>
                            </DropdownMenuTrigger>
                            <FrameOptions />
                          </DropdownMenu>
                        </div>
                        <div className="flex items-center">
                          <Button
                            size="icon"
                            variant={
                              (!modalActive &&
                                cursorTypeToUse === CursorType.Text) ||
                              showModal === ModalType.Text
                                ? "primary"
                                : "ghost"
                            }
                            data-tooltip-id="tooltip"
                            data-tooltip-html={getTooltipContent(
                              "Text",
                              DESIGN_TOOLBAR_SHORTCUTS.TEXT.key,
                            )}
                            data-mouse-down-id="design-tool-button"
                            className={cn(
                              "px-1.5 w-8 h-7 text-zinc-400 rounded-md relative z-10 hover:bg-transparent",
                              {
                                "text-foreground":
                                  (!modalActive &&
                                    cursorTypeToUse === CursorType.Text) ||
                                  showModal === ModalType.Text,
                              },
                            )}
                            onMouseEnter={handleButtonHover}
                            onClick={() => {
                              dispatch(
                                setCursorType({
                                  cursorType: CursorType.Text,
                                }),
                              );
                            }}
                          >
                            <TypeIcon className="w-4 h-4" strokeWidth={2.5} />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              onKeyDown={(e) => e.preventDefault()}
                              asChild
                            >
                              <Button
                                size="icon"
                                variant="ghost"
                                data-tooltip-id="tooltip"
                                data-tooltip-content="Text options"
                                className="p-0 w-auto h-7 text-zinc-400 rounded-md relative z-10 hover:bg-transparent"
                                onMouseEnter={handleButtonHover}
                              >
                                <ChevronDown className="w-3 h-3 transition-all duration-300 ease-in-out" />
                              </Button>
                            </DropdownMenuTrigger>
                            <TypographyOptions />
                          </DropdownMenu>
                        </div>
                        <Button
                          variant={
                            !modalActive && cursorTypeToUse === CursorType.Pan
                              ? "primary"
                              : "ghost"
                          }
                          size="icon"
                          data-tooltip-id="tooltip"
                          data-tooltip-html={getTooltipContent(
                            "Hand tool",
                            DESIGN_TOOLBAR_SHORTCUTS.HAND.key,
                          )}
                          data-mouse-down-id="design-tool-button"
                          className={cn(
                            "px-1.5 w-auto h-7 text-zinc-400 rounded-md relative z-10 hover:bg-transparent",
                            {
                              "text-foreground":
                                !modalActive &&
                                cursorTypeToUse === CursorType.Pan,
                            },
                          )}
                          onMouseEnter={handleButtonHover}
                          onClick={() => {
                            dispatch(setModal(ModalType.None));
                            dispatch(
                              setCursorType({
                                cursorType: CursorType.Pan,
                              }),
                            );
                          }}
                        >
                          <Hand className="w-4 h-4" />
                        </Button>
                        <Button
                          variant={
                            showModal === ModalType.Image ? "primary" : "ghost"
                          }
                          size="icon"
                          data-tooltip-id="tooltip"
                          data-tooltip-html={getTooltipContent(
                            "Generate image",
                            DESIGN_TOOLBAR_SHORTCUTS.GENERATE_IMAGE.key,
                          )}
                          data-mouse-down-id="design-tool-button"
                          className={cn(
                            "px-1.5 w-auto h-7 text-zinc-400 rounded-md relative z-10 hover:bg-transparent",
                            {
                              "text-foreground": showModal === ModalType.Image,
                            },
                          )}
                          onMouseEnter={handleButtonHover}
                          onClick={() => {
                            if (showModal === ModalType.Image) {
                              dispatch(setModal(ModalType.None));
                            } else {
                              dispatch(setModal(ModalType.Image));
                              dispatch(
                                setCursorType({
                                  cursorType: CursorType.Pointer,
                                }),
                              );
                            }
                          }}
                        >
                          <ImageIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-tooltip-id="tooltip"
                          data-tooltip-html={
                            !firstLoadDone
                              ? "Loading design tokens..."
                              : getTooltipContent(
                                  `Switch to ${canvasDarkMode ? "light" : "dark"} mode`,
                                  DESIGN_TOOLBAR_SHORTCUTS
                                    .TOGGLE_DARK_LIGHT_MODE.key,
                                )
                          }
                          data-mouse-down-id="design-tool-button"
                          className={cn(
                            "group px-1.5 w-auto h-7 rounded-md relative z-10 hover:bg-transparent",
                            !firstLoadDone && " cursor-not-allowed",
                          )}
                          onMouseEnter={handleButtonHover}
                          onClick={
                            !firstLoadDone
                              ? undefined
                              : handleCanvasDarkModeToggle
                          }
                          aria-label={`Switch to ${
                            canvasDarkMode ? "light" : "dark"
                          } mode`}
                        >
                          {canvasDarkMode ? (
                            <Sun className="w-4 h-4 text-zinc-400 group-hover:text-foreground transition-colors" />
                          ) : (
                            <Moon className="w-4 h-4 text-zinc-400 group-hover:text-foreground transition-colors" />
                          )}
                        </Button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
                {/* Second divider - always visible, hides first on contract, shows last on expand */}
                <AnimatePresence mode="sync">
                  <motion.div
                    key="divider-2"
                    initial={{ opacity: 0, scaleY: 0 }}
                    animate={{
                      opacity: 1,
                      scaleY: 1,
                      transition: {
                        delay: cursorType === CursorType.Drive ? 0.3 : 0,
                        duration: 0.25,
                      },
                    }}
                    exit={{
                      opacity: 0,
                      scaleY: 0,
                      transition: { duration: 0.08 },
                    }}
                    className="w-px self-stretch -my-2 mx-1 bg-white/5"
                  />
                </AnimatePresence>
                {/* InteractDesignToggle - static */}
                <InteractDesignToggle
                  className="border-0"
                  onMouseEnter={handleToolbarMouseLeave}
                  onToggleClick={handleToggleClick}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </nav>
  );
}

export default memo(DesignToolsPicker);
